import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { concatMap, map, toArray } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Minimal shape this service needs from a production order. */
export interface ProductionOrderDTO {
  productionOrderID: number;
  orderNumber: string;
  finishedGoodItemID: number;
  quantityToProduce: number;
  quantitySold?: number;
  status: 'Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold' | 'Partially Sold';
  warehouseID: number;
  actualEndDate?: string;
  [key: string]: any;
}

export interface SellOptions {
  isService: boolean;
  costPerUnit?: number;
  userID?: number;
}

/**
 * Shared home for "sell finished goods" logic, used by both the Manufacturing
 * dashboard's own Sell modal and the Products page's quick-sell button — see
 * ManufacturingSupplyChainDashboardComponent.consumeFinishedGoods, which this
 * was factored out of.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductionSaleService {
  constructor(private http: HttpClient) {}

  private authHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  private getUserIdFromToken(): number {
    const token = localStorage.getItem('authToken');
    if (!token) return 1;
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      return tokenPayload.userId || 1;
    } catch {
      return 1;
    }
  }

  /** Remaining (unsold) quantity on an order. */
  remainingQuantity(order: ProductionOrderDTO): number {
    return order.quantityToProduce - (order.quantitySold || 0);
  }

  /**
   * Orders for `itemId` that still have unsold stock, oldest completed first
   * (FIFO) — `actualEndDate` when present, else insertion/order-id order.
   */
  getSellableOrders(itemId: number): Observable<ProductionOrderDTO[]> {
    return this.http
      .get<ProductionOrderDTO[]>(`${environment.apiUrl}/api/ProductionOrders/GetAll`, { headers: this.authHeaders() })
      .pipe(
        map((orders) =>
          (orders || [])
            .filter(
              (o) =>
                o.finishedGoodItemID === itemId &&
                (o.status === 'Completed' || o.status === 'Partially Sold') &&
                this.remainingQuantity(o) > 0
            )
            .sort((a, b) => {
              const dateA = a.actualEndDate ? new Date(a.actualEndDate).getTime() : 0;
              const dateB = b.actualEndDate ? new Date(b.actualEndDate).getTime() : 0;
              if (dateA !== dateB) return dateA - dateB;
              return a.productionOrderID - b.productionOrderID;
            })
        )
      );
  }

  /** Total unsold quantity across an item's sellable orders. */
  getAvailableToSell(itemId: number): Observable<number> {
    return this.getSellableOrders(itemId).pipe(
      map((orders) => orders.reduce((sum, o) => sum + this.remainingQuantity(o), 0))
    );
  }

  /**
   * Marks `quantityToSell` units of a single order as sold: updates the
   * order's status/quantitySold, and — unless the item is a Service, which
   * holds no stock — posts a matching negative 'Sale' inventory transaction.
   */
  sellFromOrder(order: ProductionOrderDTO, quantityToSell: number, opts: SellOptions): Observable<ProductionOrderDTO> {
    const headers = this.authHeaders();
    const newQuantitySold = (order.quantitySold || 0) + quantityToSell;
    const isFullySold = newQuantitySold >= order.quantityToProduce;

    const updatedOrder = {
      ...order,
      status: isFullySold ? 'Sold' : 'Partially Sold',
      quantitySold: newQuantitySold,
      actualEndDate: isFullySold ? new Date().toISOString() : order.actualEndDate,
    };

    return this.http
      .put<ProductionOrderDTO>(
        `${environment.apiUrl}/api/ProductionOrders/Update?productionOrderId=${order.productionOrderID}`,
        updatedOrder,
        { headers }
      )
      .pipe(
        concatMap((updated) => {
          // A Service holds no stock, so selling one must not write a stock
          // movement — it would drive the item negative. The order status
          // change above is the whole record of the sale.
          if (opts.isService) return of(updated);

          const transaction = {
            itemID: order.finishedGoodItemID,
            warehouseID: order.warehouseID || 1,
            transactionType: 'Sale',
            quantityChange: -quantityToSell,
            costPerUnit: opts.costPerUnit,
            transactionDate: new Date().toISOString(),
            referenceType: 'ProductionOrder',
            referenceID: order.productionOrderID.toString(),
            userID: opts.userID ?? this.getUserIdFromToken(),
            notes: `Sold/Consumed from Production Order ${order.orderNumber}`,
            batchNumber: '',
            expiryDate: null,
            referenceTypeCode: 'SALE',
          };

          return this.http
            .post(`${environment.apiUrl}/api/InventoryTransactions/Add`, transaction, { headers })
            .pipe(map(() => updated));
        })
      );
  }

  /**
   * Sells `quantity` units of `itemId`, spread FIFO across its sellable
   * orders. Emits the orders that were touched, in the order they were sold.
   * Errors (including "not enough stock") propagate to the caller unchanged.
   */
  sellQuantity(itemId: number, quantity: number, opts: SellOptions): Observable<ProductionOrderDTO[]> {
    return this.getSellableOrders(itemId).pipe(
      concatMap((orders) => {
        const available = orders.reduce((sum, o) => sum + this.remainingQuantity(o), 0);
        if (quantity > available) {
          throw new Error(`Cannot sell more than ${available} units`);
        }

        let remaining = quantity;
        const allocations: { order: ProductionOrderDTO; qty: number }[] = [];
        for (const order of orders) {
          if (remaining <= 0) break;
          const take = Math.min(remaining, this.remainingQuantity(order));
          if (take > 0) {
            allocations.push({ order, qty: take });
            remaining -= take;
          }
        }

        return of(...allocations).pipe(
          concatMap(({ order, qty }) => this.sellFromOrder(order, qty, opts)),
          toArray()
        );
      })
    );
  }
}
